import { hasFileProxyPath } from '@Core/lib/tasks/utils/file';
import { FileImage } from '@Pages/pageRuntime/components/Image';
import {
  SOURCE_ATTRIBUTE,
  serializeSourceMetadata
} from '@Pages/pageRuntime/designMode/sourceMetadata';
import React from 'react';

type JsxDevSource = {
  fileName?: unknown;
  lineNumber?: unknown;
  columnNumber?: unknown;
};

/**
 * A raw `<img>` whose source has the file proxy path renders through
 * `FileImage`. `FileImage` manages the source only when its host is trusted,
 * and otherwise renders the authored image unchanged.
 */
export function runtimeElementType(type: unknown, props: unknown): unknown {
  if (type !== 'img' || props === null || typeof props !== 'object') {
    return type;
  }

  const src = (props as { src?: unknown }).src;

  return typeof src === 'string' && hasFileProxyPath(src) ? FileImage : type;
}

export function createRuntimeReact(authoring: boolean): typeof React {
  const createElement = ((type: unknown, props: unknown, ...children: unknown[]) => {
    const elementType = runtimeElementType(type, props) as React.ElementType;

    if (authoring && props && typeof props === 'object' && '__source' in props) {
      const { __source: source, __self: _self, ...rest } = props as Record<string, unknown>;
      const devSource = source as JsxDevSource | null;

      if (type !== React.Fragment && devSource && typeof devSource.fileName === 'string') {
        rest[SOURCE_ATTRIBUTE] = serializeSourceMetadata(
          devSource.fileName,
          typeof devSource.lineNumber === 'number' ? devSource.lineNumber : 0,
          typeof devSource.columnNumber === 'number' ? devSource.columnNumber : 0
        );
      }

      return React.createElement(elementType, rest, ...(children as React.ReactNode[]));
    }

    return React.createElement(
      elementType,
      props as object | null,
      ...(children as React.ReactNode[])
    );
  }) as typeof React.createElement;

  return { ...React, createElement };
}

/** The React that published page code runs with: raw file images render through `FileImage`. */
export const publishedReact = createRuntimeReact(false);
